import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import type {
    PubMedExportWorkbookData,
    PubMedTlrReviewRow,
    PubMedExtractionLabRow,
    PubMedConfigRow,
    PubMedDashboardMetricRow,
} from './pubmedExportRows'

type TlrColumnConfig = {
    header: string
    key: keyof PubMedTlrReviewRow
    width: number
    wrap?: boolean
    family:
        | 'ranking'
        | 'screening'
        | 'atlasMatch'
        | 'studyDetails'
        | 'categoryFlags'
        | 'outcomeText'
        | 'objectiveConclusion'
        | 'identifiers'
}

type ExtractionColumnConfig = {
    header: string
    key: keyof PubMedExtractionLabRow
    width: number
    wrap?: boolean
    family: 'studyDetails' | 'reviewFields' | 'identifiers'
}

const SCREENING_VALIDATION = ['-', 'Y', 'N', '?']

const TLR_COLUMNS: TlrColumnConfig[] = [
    { header: '#', key: 'searchId', width: 8, family: 'ranking' },
    { header: 'PMID', key: 'pmid', width: 14, family: 'identifiers' },
    { header: 'Search Terms Score', key: 'searchTermsScore', width: 16, family: 'ranking' },
    { header: 'Keyword Score', key: 'customKeywordsScore', width: 14, family: 'ranking' },

    { header: 'Title Screening', key: 'titleScreening', width: 16, family: 'screening' },
    { header: 'Abstract Screening', key: 'abstractScreening', width: 18, family: 'screening' },
    { header: 'Full Text Screening', key: 'fullTextScreening', width: 17, family: 'screening' },

    { header: 'ATLAS Match', key: 'atlasMatch', width: 12, family: 'atlasMatch' },
    { header: 'ATLAS Collection', key: 'atlasCollection', width: 16, family: 'atlasMatch' },
    { header: 'ATLAS Matched On', key: 'atlasMatchedOn', width: 15, family: 'atlasMatch' },

    { header: 'Sponsor Company', key: 'sponsorCompany', width: 24, wrap: true, family: 'studyDetails' },
    { header: 'Title', key: 'title', width: 60, wrap: true, family: 'studyDetails' },
    { header: 'Authors', key: 'authors', width: 32, wrap: true, family: 'studyDetails' },
    { header: 'Author Affiliations', key: 'authorAffiliations', width: 45, wrap: true, family: 'studyDetails' },
    { header: 'Year', key: 'year', width: 10, family: 'studyDetails' },
    { header: 'Citation', key: 'citation', width: 36, wrap: true, family: 'studyDetails' },
    { header: 'Publication Types', key: 'publicationTypes', width: 24, wrap: true, family: 'studyDetails' },
    { header: 'Abstract', key: 'abstract', width: 100, wrap: true, family: 'studyDetails' },
    { header: 'Drugs / Interventions', key: 'drugsInterventions', width: 28, wrap: true, family: 'studyDetails' },
    { header: 'Manual Search Outcomes', key: 'manualSearchOutcomes', width: 28, wrap: true, family: 'studyDetails' },

    {
        header: 'Epidemiology',
        key: 'epidemiology',
        width: 14,
        family: 'categoryFlags',
    },
    {
        header: 'Epidemiology Measures',
        key: 'epidemiologyMeasures',
        width: 32,
        wrap: true,
        family: 'outcomeText',
    },
    {
        header: 'Epidemiology Measure Variants',
        key: 'epidemiologyThresholds',
        width: 28,
        wrap: true,
        family: 'outcomeText',
    },

    {
        header: 'Clinical Efficacy/ Effectiveness',
        key: 'clinicalEfficacy',
        width: 16,
        family: 'categoryFlags',
    },
    {
        header: 'Clinical Measures',
        key: 'clinicalEfficacyMeasures',
        width: 34,
        wrap: true,
        family: 'outcomeText',
    },
    {
        header: 'Clinical Measure Variants',
        key: 'clinicalEfficacyThresholds',
        width: 30,
        wrap: true,
        family: 'outcomeText',
    },

    {
        header: 'PROs',
        key: 'humanistic',
        width: 14,
        family: 'categoryFlags',
    },
    {
        header: 'PRO Measures',
        key: 'humanisticMeasures',
        width: 32,
        wrap: true,
        family: 'outcomeText',
    },
    {
        header: 'PRO Variants',
        key: 'humanisticThresholds',
        width: 28,
        wrap: true,
        family: 'outcomeText',
    },

    {
        header: 'Economic',
        key: 'economic',
        width: 12,
        family: 'categoryFlags',
    },
    {
        header: 'Economic Measures',
        key: 'economicMeasures',
        width: 32,
        wrap: true,
        family: 'outcomeText',
    },
    {
        header: 'Economic Measure Variants',
        key: 'economicThresholds',
        width: 28,
        wrap: true,
        family: 'outcomeText',
    },

    {
        header: 'Treatment Patterns',
        key: 'treatmentPatterns',
        width: 18,
        family: 'categoryFlags',
    },
    {
        header: 'Treatment Pattern Measures',
        key: 'treatmentPatternsMeasures',
        width: 34,
        wrap: true,
        family: 'outcomeText',
    },
    {
        header: 'Treatment Pattern Variants',
        key: 'treatmentPatternsThresholds',
        width: 30,
        wrap: true,
        family: 'outcomeText',
    },

    {
        header: 'Safety',
        key: 'safety',
        width: 10,
        family: 'categoryFlags',
    },
    {
        header: 'Safety Measures',
        key: 'safetyMeasures',
        width: 30,
        wrap: true,
        family: 'outcomeText',
    },
    {
        header: 'Safety Variants',
        key: 'safetyThresholds',
        width: 28,
        wrap: true,
        family: 'outcomeText',
    },

    {
        header: 'Keyword Hierarchy',
        key: 'keywordHierarchy',
        width: 52,
        wrap: true,
        family: 'outcomeText',
    },
    {
        header: 'Keyword Matched Terms',
        key: 'keywordMatchedTerms',
        width: 38,
        wrap: true,
        family: 'outcomeText',
    },
    {
        header: 'Keyword Matched Fields',
        key: 'keywordMatchedFields',
        width: 24,
        wrap: true,
        family: 'outcomeText',
    },

    {
        header: 'Objective',
        key: 'objective',
        width: 42,
        wrap: true,
        family: 'objectiveConclusion',
    },
    {
        header: 'Messaging / Conclusion',
        key: 'messagingConclusion',
        width: 50,
        wrap: true,
        family: 'objectiveConclusion',
    },

    { header: 'DOI', key: 'doi', width: 24, family: 'identifiers' },
    { header: 'PMCID', key: 'pmcId', width: 14, family: 'identifiers' },
    { header: 'Full Text Link', key: 'fullTextLink', width: 18, family: 'identifiers' },
]

const EXTRACTION_COLUMNS: ExtractionColumnConfig[] = [
    { header: '#', key: 'searchId', width: 8, family: 'identifiers' },
    { header: 'PMID', key: 'pmid', width: 14, family: 'identifiers' },
    { header: 'PMCID', key: 'pmcId', width: 14, family: 'identifiers' },
    { header: 'DOI', key: 'doi', width: 24, family: 'identifiers' },
    { header: 'Title', key: 'title', width: 55, wrap: true, family: 'studyDetails' },
    { header: 'Citation', key: 'citation', width: 36, wrap: true, family: 'studyDetails' },
    { header: 'Full Text Link', key: 'fullTextLink', width: 18, family: 'identifiers' },
    { header: 'Abstract', key: 'abstract', width: 100, wrap: true, family: 'studyDetails' },
    { header: 'Sponsor Company', key: 'sponsorCompany', width: 24, wrap: true, family: 'studyDetails' },
    { header: 'Authors', key: 'authors', width: 32, wrap: true, family: 'studyDetails' },
    { header: 'Author Affiliations', key: 'authorAffiliations', width: 45, wrap: true, family: 'studyDetails' },

    { header: 'Study Design Sentence', key: 'studyDesignSentence', width: 55, wrap: true, family: 'reviewFields' },
    { header: 'Study Design Tags', key: 'studyDesignTags', width: 24, wrap: true, family: 'reviewFields' },

    {
        header: 'Primary Outcome Sentence',
        key: 'primaryOutcomeSentence',
        width: 55,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'Secondary Outcome Sentence',
        key: 'secondaryOutcomeSentence',
        width: 55,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'Exploratory Outcome Sentence',
        key: 'exploratoryOutcomeSentence',
        width: 55,
        wrap: true,
        family: 'reviewFields',
    },
    { header: 'Outcome Sentences', key: 'outcomeSentences', width: 70, wrap: true, family: 'reviewFields' },

    {
        header: 'Study Population Sentence',
        key: 'studyPopulationSentence',
        width: 55,
        wrap: true,
        family: 'reviewFields',
    },

    { header: 'Sample Size Sentence', key: 'sampleSizeSentence', width: 45, wrap: true, family: 'reviewFields' },
    { header: 'Sample Size Value', key: 'sampleSizeValue', width: 18, family: 'reviewFields' },
    { header: 'Total Sample Size', key: 'totalSampleSize', width: 18, family: 'reviewFields' },
    { header: 'Group Sample Sizes', key: 'groupSampleSizes', width: 28, wrap: true, family: 'reviewFields' },

    {
        header: 'Inclusion Criteria Sentence',
        key: 'inclusionCriteriaSentence',
        width: 55,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'Exclusion Criteria Sentence',
        key: 'exclusionCriteriaSentence',
        width: 55,
        wrap: true,
        family: 'reviewFields',
    },
    { header: 'Age Sentence', key: 'ageSentence', width: 40, wrap: true, family: 'reviewFields' },

    {
        header: 'Cost Adjustment Sentence',
        key: 'costAdjustmentSentence',
        width: 55,
        wrap: true,
        family: 'reviewFields',
    },
    { header: 'Cost Currency Years', key: 'costCurrencyYears', width: 20, family: 'reviewFields' },

    {
        header: 'Economic Outcome Sentences',
        key: 'economicOutcomeSentences',
        width: 65,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'Cost Result Sentences',
        key: 'costResultSentences',
        width: 65,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'Cost Type Tags',
        key: 'costTypeTags',
        width: 24,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'Resource Use Sentences',
        key: 'resourceUseSentences',
        width: 60,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'Resource Use Tags',
        key: 'resourceUseTags',
        width: 24,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'Cost-Effectiveness Sentence',
        key: 'costEffectivenessSentence',
        width: 60,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'ICER Sentence',
        key: 'icerSentence',
        width: 55,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'ICER Value',
        key: 'icerValue',
        width: 18,
        family: 'reviewFields',
    },
    {
        header: 'Budget Impact Sentence',
        key: 'budgetImpactSentence',
        width: 55,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'Budget Impact Value',
        key: 'budgetImpactValue',
        width: 20,
        family: 'reviewFields',
    },
    {
        header: 'PMPM / PPPM Sentence',
        key: 'pmpmPppmSentence',
        width: 45,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'PMPM / PPPM Value',
        key: 'pmpmPppmValue',
        width: 18,
        family: 'reviewFields',
    },
    {
        header: 'Inpatient Cost Sentence',
        key: 'inpatientCostSentence',
        width: 45,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'Outpatient Cost Sentence',
        key: 'outpatientCostSentence',
        width: 45,
        wrap: true,
        family: 'reviewFields',
    },
    {
        header: 'Pharmacy Cost Sentence',
        key: 'pharmacyCostSentence',
        width: 45,
        wrap: true,
        family: 'reviewFields',
    },

    { header: 'Data Source Sentence', key: 'dataSourceSentence', width: 55, wrap: true, family: 'reviewFields' },
    { header: 'Data Source Types', key: 'dataSourceTypes', width: 20, wrap: true, family: 'reviewFields' },

    { header: 'Comparator Sentence', key: 'comparatorSentence', width: 55, wrap: true, family: 'reviewFields' },
    { header: 'Follow-up Sentence', key: 'followUpSentence', width: 45, wrap: true, family: 'reviewFields' },

    { header: 'Time Period Sentence', key: 'timePeriodSentence', width: 55, wrap: true, family: 'reviewFields' },
    { header: 'Time Period Start', key: 'timePeriodStart', width: 18, family: 'reviewFields' },
    { header: 'Time Period End', key: 'timePeriodEnd', width: 18, family: 'reviewFields' },

    { header: 'Study Duration Sentence', key: 'studyDurationSentence', width: 55, wrap: true, family: 'reviewFields' },
    { header: 'Study Duration Value', key: 'studyDurationValue', width: 20, family: 'reviewFields' },

    { header: 'Objective Sentence', key: 'objectiveSentence', width: 55, wrap: true, family: 'reviewFields' },
    { header: 'Conclusion Sentence', key: 'conclusionSentence', width: 55, wrap: true, family: 'reviewFields' },

    { header: 'Geography Sentence', key: 'geographySentence', width: 34, wrap: true, family: 'reviewFields' },
    { header: 'Setting Sentence', key: 'settingSentence', width: 34, wrap: true, family: 'reviewFields' },
    { header: 'RWE Sentence', key: 'realWorldEvidenceSentence', width: 40, wrap: true, family: 'reviewFields' },
    { header: 'PRO/HRQoL Sentence', key: 'proHrqolSentence', width: 40, wrap: true, family: 'reviewFields' },
    { header: 'Safety Sentence', key: 'safetySentence', width: 40, wrap: true, family: 'reviewFields' },
]

function fillByFamily(
    family:
        | 'ranking'
        | 'screening'
        | 'atlasMatch'
        | 'studyDetails'
        | 'categoryFlags'
        | 'outcomeText'
        | 'objectiveConclusion'
        | 'identifiers'
        | 'reviewFields'
): ExcelJS.Fill {
    const makeFill = (argb: string): ExcelJS.Fill =>
        ({
            type: 'pattern' as const,
            pattern: 'solid' as const,
            fgColor: { argb },
        }) as ExcelJS.Fill

    switch (family) {
        case 'ranking':
            return makeFill('E8F3E6')
        case 'screening':
            return makeFill('DCEBFA')
        case 'atlasMatch':
            return makeFill('FDE2E1')
        case 'studyDetails':
            return makeFill('EAF2F8')
        case 'categoryFlags':
            return makeFill('E6F4EA')
        case 'outcomeText':
            return makeFill('FDF2D0')
        case 'objectiveConclusion':
            return makeFill('EDE4F7')
        case 'identifiers':
            return makeFill('ECECEC')
        case 'reviewFields':
            return makeFill('F4ECFB')
        default:
            return makeFill('FFFFFF')
    }
}

function styleHeaderRow<T extends { family: string }>(
    worksheet: ExcelJS.Worksheet,
    columns: Array<T & { header: string }>
) {
    const headerRow = worksheet.getRow(1)
    headerRow.height = 24

    columns.forEach((col, index) => {
        const cell = headerRow.getCell(index + 1)
        cell.font = { bold: true, color: { argb: '1F1F1F' } }
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
        cell.fill = fillByFamily(col.family as any)
        cell.border = {
            top: { style: 'thin', color: { argb: 'BFBFBF' } },
            left: { style: 'thin', color: { argb: 'BFBFBF' } },
            bottom: { style: 'thin', color: { argb: 'BFBFBF' } },
            right: { style: 'thin', color: { argb: 'BFBFBF' } },
        }
    })
}

function addRowBordersAndAlignment(worksheet: ExcelJS.Worksheet, wrapMap: boolean[]) {
    worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return // skip header

        row.height = 100 // Trade-off knowing that we are sometimes cutting off a good portion of the abstract

        row.eachCell((cell, colNumber) => {
            cell.alignment = {
                vertical: 'top',
                wrapText: !!wrapMap[colNumber - 1],
            }
            cell.border = {
                bottom: { style: 'thin', color: { argb: 'E0E0E0' } },
            }
        })
    })
}

function setHyperlinkCellText(row: Record<string, any>, key: string, text = 'Open link') {
    if (row[key]) {
        row[key] = {
            text,
            hyperlink: row[key],
        }
    }
}

function addScreeningValidation(worksheet: ExcelJS.Worksheet, rowCount: number, columnNumbers: number[]) {
    for (let row = 2; row <= rowCount + 1; row++) {
        for (const col of columnNumbers) {
            worksheet.getCell(row, col).dataValidation = {
                type: 'list',
                allowBlank: false,
                formulae: [`"${SCREENING_VALIDATION.join(',')}"`],
                showErrorMessage: true,
                errorTitle: 'Invalid value',
                error: 'Select one of: -, Y, N, ?',
            }
        }
    }
}

function autosetWorksheetView(worksheet: ExcelJS.Worksheet) {
    worksheet.views = [{ state: 'frozen', ySplit: 1 }]
}

function getExcelColumnLetter(columnNumber: number): string {
    let dividend = columnNumber
    let columnName = ''

    while (dividend > 0) {
        const modulo = (dividend - 1) % 26
        columnName = String.fromCharCode(65 + modulo) + columnName
        dividend = Math.floor((dividend - modulo) / 26)
    }

    return columnName
}

function getColumnLetterByKey<T extends { key: string }>(columns: T[], key: string): string {
    const columnIndex = columns.findIndex((c) => c.key === key)
    if (columnIndex === -1) return ''
    return getExcelColumnLetter(columnIndex + 1)
}

function getImageExtension(base64: string): 'png' | 'jpeg' {
    if (base64.startsWith('data:image/jpeg') || base64.startsWith('data:image/jpg')) {
        return 'jpeg'
    }
    return 'png'
}

function addWorksheetFromRows<T extends Record<string, any>>(
    workbook: ExcelJS.Workbook,
    sheetName: string,
    rows: T[],
    columns: Array<{ header: string; key: keyof T; width: number; wrap?: boolean; family: string }>,
    hyperlinkColumns: Array<{ key: keyof T; text?: string }> = []
): ExcelJS.Worksheet {
    const worksheet = workbook.addWorksheet(sheetName)
    autosetWorksheetView(worksheet)

    worksheet.columns = columns.map((col) => ({
        header: col.header,
        key: String(col.key),
        width: col.width,
    }))

    worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: columns.length },
    }

    rows.forEach((rawRow) => {
        const row = { ...rawRow } as Record<string, any>

        for (const hyperlink of hyperlinkColumns) {
            setHyperlinkCellText(row, String(hyperlink.key), hyperlink.text || 'Open link')
        }

        worksheet.addRow(row)
    })

    styleHeaderRow(worksheet, columns as any)
    addRowBordersAndAlignment(
        worksheet,
        columns.map((c) => !!c.wrap)
    )

    return worksheet
}

function addConfigSheet(workbook: ExcelJS.Workbook, rows: PubMedConfigRow[]): ExcelJS.Worksheet {
    const worksheet = workbook.addWorksheet('Config')
    autosetWorksheetView(worksheet)

    worksheet.columns = [
        { header: 'Section', key: 'section', width: 28 },
        { header: 'Key', key: 'key', width: 34 },
        { header: 'Value', key: 'value', width: 80 },
    ]

    worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: 3 },
    }

    rows.forEach((row) => worksheet.addRow(row))

    styleHeaderRow(worksheet, [
        { header: 'Section', family: 'screening' },
        { header: 'Key', family: 'studyDetails' },
        { header: 'Value', family: 'reviewFields' },
    ] as any)

    worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return
        row.eachCell((cell) => {
            cell.alignment = { vertical: 'top', wrapText: true }
            cell.border = {
                bottom: { style: 'thin', color: { argb: 'E0E0E0' } },
            }
        })
    })

    return worksheet
}

async function addSummarySheet(
    workbook: ExcelJS.Workbook,
    workbookData: PubMedExportWorkbookData
): Promise<ExcelJS.Worksheet> {
    const worksheet = workbook.addWorksheet('Summary')

    worksheet.getRow(2).height = 40
    worksheet.getRow(3).height = 40

    worksheet.columns = [{ width: 24 }, { width: 90 }, { width: 18 }, { width: 18 }]

    const summary = workbookData.summarySheet || {}

    // Row 3 positioning (B3 area)
    const baseRow = 2

    // --- CLIENT LOGO (B2) ---
    if (summary.orgLogoData) {
        const orgLogoId = workbook.addImage({
            base64: summary.orgLogoData,
            extension: getImageExtension(summary.orgLogoData),
        })

        worksheet.addImage(orgLogoId, {
            tl: { col: 1, row: 1 },
            ext: { width: 140, height: 40 },
        })
    }

    // --- ARYSANA LOGO (B3) ---
    if (summary.arysanaLogoData) {
        const arysanaLogoId = workbook.addImage({
            base64: summary.arysanaLogoData,
            extension: getImageExtension(summary.arysanaLogoData),
        })

        worksheet.addImage(arysanaLogoId, {
            tl: { col: 1, row: 2 },
            ext: { width: 140, height: 40 },
        })
    }

    const companyName = summary.companyName || 'Arysana'
    const orgName = summary.orgName || ''
    const exportedAt = summary.exportedAt || new Date().toLocaleString()

    worksheet.mergeCells('A2:A3')
    worksheet.getCell('A2').value = 'ATLAS PubMed Export Summary'
    worksheet.getCell('A2').font = { bold: true, size: 16 }
    worksheet.getCell('A2').alignment = {
        vertical: 'middle',
        horizontal: 'left',
        wrapText: true,
    }

    worksheet.getCell('A4').value = orgName ? `${orgName} | ${companyName}` : companyName
    worksheet.getCell('A4').font = { italic: true, size: 12 }

    worksheet.getCell('A6').value = 'Export Date'
    worksheet.getCell('B6').value = exportedAt

    worksheet.getCell('A8').value = 'Search Summary'
    worksheet.getCell('A8').font = { bold: true }

    worksheet.getCell('A9').value = 'Search String'
    worksheet.getCell('B9').value = summary.searchString || ''

    worksheet.getCell('A10').value = 'Disease Area'
    worksheet.getCell('B10').value = summary.diseaseArea || ''

    worksheet.getCell('A11').value = 'Interventions'
    worksheet.getCell('B11').value = summary.interventions || ''

    worksheet.getCell('A12').value = 'Outcomes'
    worksheet.getCell('B12').value = summary.outcomes || ''

    worksheet.getCell('A13').value = 'Date Range'
    worksheet.getCell('B13').value = summary.dateRange || ''

    worksheet.getCell('A14').value = 'Article Types'
    worksheet.getCell('B14').value = summary.articleTypes || ''

    worksheet.getCell('A16').value = 'Screening Summary'
    worksheet.getCell('A16').font = { bold: true }

    const titleCol = getColumnLetterByKey(TLR_COLUMNS as Array<{ key: string }>, 'titleScreening')
    const abstractCol = getColumnLetterByKey(TLR_COLUMNS as Array<{ key: string }>, 'abstractScreening')
    const fullTextCol = getColumnLetterByKey(TLR_COLUMNS as Array<{ key: string }>, 'fullTextScreening')

    const tlrStartRow = 2
    const tlrEndRow = Math.max(2, workbookData.tlrRows.length + 1)

    const titleRange = `'TLR Review'!${titleCol}${tlrStartRow}:${titleCol}${tlrEndRow}`
    const abstractRange = `'TLR Review'!${abstractCol}${tlrStartRow}:${abstractCol}${tlrEndRow}`
    const fullTextRange = `'TLR Review'!${fullTextCol}${tlrStartRow}:${fullTextCol}${tlrEndRow}`

    worksheet.getCell('A17').value = 'Title Included'
    worksheet.getCell('B17').value = { formula: `COUNTIF(${titleRange},"Y")` }

    worksheet.getCell('A18').value = 'Title Excluded'
    worksheet.getCell('B18').value = { formula: `COUNTIF(${titleRange},"N")` }

    worksheet.getCell('A19').value = 'Title Pending / Unsure'
    worksheet.getCell('B19').value = {
        formula: `COUNTIFS(${titleRange},"<>Y",${titleRange},"<>N")`,
    }

    worksheet.getCell('A21').value = 'Abstract Included'
    worksheet.getCell('B21').value = { formula: `COUNTIF(${abstractRange},"Y")` }

    worksheet.getCell('A22').value = 'Abstract Excluded'
    worksheet.getCell('B22').value = { formula: `COUNTIF(${abstractRange},"N")` }

    worksheet.getCell('A23').value = 'Abstract Pending / Unsure'
    worksheet.getCell('B23').value = {
        formula: `COUNTIFS(${titleRange},"<>N",${abstractRange},"<>Y",${abstractRange},"<>N")`,
    }

    worksheet.getCell('A25').value = 'Full Text Included'
    worksheet.getCell('B25').value = { formula: `COUNTIF(${fullTextRange},"Y")` }

    worksheet.getCell('A26').value = 'Full Text Excluded'
    worksheet.getCell('B26').value = { formula: `COUNTIF(${fullTextRange},"N")` }

    worksheet.getCell('A27').value = 'Full Text Pending / Unsure'
    worksheet.getCell('B27').value = {
        formula: `COUNTIFS(${titleRange},"<>N",${abstractRange},"<>N",${fullTextRange},"<>Y",${fullTextRange},"<>N")`,
    }

    for (let row = 1; row <= 27; row++) {
        const labelCell = worksheet.getCell(`A${row}`)
        const valueCell = worksheet.getCell(`B${row}`)

        if ([3, 8, 16].includes(row)) {
            labelCell.fill = fillByFamily('objectiveConclusion')
        } else if ([6, 9, 10, 11, 12, 13, 14].includes(row)) {
            labelCell.fill = fillByFamily('studyDetails')
        } else if (row >= 17 && row <= 27) {
            labelCell.fill = fillByFamily('screening')
        }

        labelCell.font =
            row === 3
                ? { bold: true, size: 16 }
                : row === 4
                  ? { italic: true, size: 12 }
                  : { bold: [8, 16].includes(row) }

        labelCell.alignment = { vertical: 'top', wrapText: true }
        valueCell.alignment = { vertical: 'top', wrapText: true }

        labelCell.border = {
            bottom: { style: 'thin', color: { argb: 'E0E0E0' } },
        }
        valueCell.border = {
            bottom: { style: 'thin', color: { argb: 'E0E0E0' } },
        }
    }

    const lastRow = worksheet.lastRow?.number || 20
    const footerRow = lastRow + 2

    const cell = worksheet.getCell(`B${footerRow}`)
    cell.value = "Generated with Arysana's ATLAS"
    cell.font = {
        italic: true,
        size: 10,
        color: { argb: '595959' },
    }
    cell.alignment = { horizontal: 'right' }

    await worksheet.protect('Arysana', {
        selectLockedCells: true,
        selectUnlockedCells: true,
    })

    return worksheet
}

function addDashboardSheet(
    workbook: ExcelJS.Workbook,
    summaryMetrics: PubMedDashboardMetricRow[],
    shareOfVoiceRows: Array<Record<string, string | number>>,
    shareOfVoiceByYearRows: Array<Record<string, string | number>>,
    categoryByCompanyRows: Array<Record<string, string | number>>,
    outcomeByCompanyRows: Array<Record<string, string | number>>,
    messagingByCompanyRows: Array<Record<string, string>>
): ExcelJS.Worksheet {
    const worksheet = workbook.addWorksheet('Insights')

    worksheet.getCell('A1').value = 'PubMed Insights Dashboard'
    worksheet.getCell('A1').font = { bold: true, size: 14 }

    let currentRow = 3

    worksheet.getCell(currentRow, 1).value = 'Summary Metrics'
    worksheet.getCell(currentRow, 1).font = { bold: true }
    currentRow += 1

    summaryMetrics.forEach((metric) => {
        worksheet.getCell(currentRow, 1).value = metric.metric
        worksheet.getCell(currentRow, 2).value = metric.value
        currentRow += 1
    })

    currentRow += 2
    worksheet.getCell(currentRow, 1).value = 'Share of Voice'
    worksheet.getCell(currentRow, 1).font = { bold: true }
    currentRow += 1

    if (shareOfVoiceRows.length > 0) {
        const headers = Object.keys(shareOfVoiceRows[0])
        headers.forEach((header, idx) => {
            worksheet.getCell(currentRow, idx + 1).value = header
            worksheet.getCell(currentRow, idx + 1).font = { bold: true }
            worksheet.getCell(currentRow, idx + 1).fill = fillByFamily('atlasMatch')
        })
        currentRow += 1

        shareOfVoiceRows.forEach((row) => {
            headers.forEach((header, idx) => {
                worksheet.getCell(currentRow, idx + 1).value = row[header]
            })
            currentRow += 1
        })
    }

    currentRow += 2
    worksheet.getCell(currentRow, 1).value = 'Share of Voice by Year'
    worksheet.getCell(currentRow, 1).font = { bold: true }
    currentRow += 1

    if (shareOfVoiceByYearRows.length > 0) {
        const firstRow = shareOfVoiceByYearRows[0]

        // Extract and sort year columns
        const yearHeaders = Object.keys(firstRow)
            .filter((key) => /^\d{4}$/.test(key))
            .sort((a, b) => Number(a) - Number(b))

        // Force correct order
        const headers = ['company', 'totalStudies', ...yearHeaders]

        // Pretty display labels
        const headerLabels: Record<string, string> = {
            company: 'Company',
            totalStudies: 'Total Studies',
        }

        // HEADER ROW
        headers.forEach((header, idx) => {
            const cell = worksheet.getCell(currentRow, idx + 1)
            cell.value = headerLabels[header] || header
            cell.font = { bold: true }
            cell.fill = fillByFamily('atlasMatch')
            cell.alignment = { horizontal: 'center', vertical: 'middle' }
        })
        currentRow += 1

        // DATA ROWS
        shareOfVoiceByYearRows.forEach((row) => {
            headers.forEach((header, idx) => {
                worksheet.getCell(currentRow, idx + 1).value = row[header] ?? ''
            })
            currentRow += 1
        })
    }

    currentRow += 2
    worksheet.getCell(currentRow, 1).value = 'Share of Voice by Category'
    worksheet.getCell(currentRow, 1).font = { bold: true }
    currentRow += 1

    if (categoryByCompanyRows.length > 0) {
        const headers = Object.keys(categoryByCompanyRows[0])
        headers.forEach((header, idx) => {
            worksheet.getCell(currentRow, idx + 1).value = header
            worksheet.getCell(currentRow, idx + 1).font = { bold: true }
            worksheet.getCell(currentRow, idx + 1).fill = fillByFamily('categoryFlags')
        })
        currentRow += 1

        categoryByCompanyRows.forEach((row) => {
            headers.forEach((header, idx) => {
                worksheet.getCell(currentRow, idx + 1).value = row[header]
            })
            currentRow += 1
        })
    }

    currentRow += 2
    worksheet.getCell(currentRow, 1).value = 'Outcome by Company'
    worksheet.getCell(currentRow, 1).font = { bold: true }
    currentRow += 1

    if (outcomeByCompanyRows.length > 0) {
        const headers = Object.keys(outcomeByCompanyRows[0])
        headers.forEach((header, idx) => {
            worksheet.getCell(currentRow, idx + 1).value = header
            worksheet.getCell(currentRow, idx + 1).font = { bold: true }
            worksheet.getCell(currentRow, idx + 1).fill = fillByFamily('outcomeText')
        })
        currentRow += 1

        outcomeByCompanyRows.forEach((row) => {
            headers.forEach((header, idx) => {
                worksheet.getCell(currentRow, idx + 1).value = row[header]
            })
            currentRow += 1
        })
    }

    const totalColumns = worksheet.columnCount

    for (let col = 1; col <= totalColumns; col++) {
        worksheet.getColumn(col).width = col === 1 ? 32 : 20
    }

    currentRow += 2
    worksheet.getCell(currentRow, 1).value = 'Messaging / Conclusion by Company'
    worksheet.getCell(currentRow, 1).font = { bold: true }
    currentRow += 1

    if (messagingByCompanyRows.length > 0) {
        const headers = Object.keys(messagingByCompanyRows[0])

        headers.forEach((header, idx) => {
            worksheet.getCell(currentRow, idx + 1).value = header
            worksheet.getCell(currentRow, idx + 1).font = { bold: true }
            worksheet.getCell(currentRow, idx + 1).fill = fillByFamily('objectiveConclusion')
        })
        currentRow += 1

        messagingByCompanyRows.forEach((row) => {
            headers.forEach((header, idx) => {
                worksheet.getCell(currentRow, idx + 1).value = row[header]
            })
            currentRow += 1
        })
    }

    worksheet.eachRow((row) => {
        row.eachCell((cell) => {
            cell.alignment = { vertical: 'top', wrapText: true }
            cell.border = {
                bottom: { style: 'thin', color: { argb: 'E0E0E0' } },
            }
        })
    })

    return worksheet
}

export async function exportPubMedResultsToExcel(
    workbookData: PubMedExportWorkbookData,
    filename = 'pubmed_review_export.xlsx'
): Promise<void> {
    const {
        tlrRows,
        extractionRows,
        configRows,
        summaryMetrics,
        shareOfVoiceRows,
        shareOfVoiceByYearRows,
        categoryByCompanyRows,
        outcomeByCompanyRows,
        messagingByCompanyRows,
    } = workbookData

    if (!tlrRows.length && !extractionRows.length) return

    const workbook = new ExcelJS.Workbook()

    const authorName = 'Arysana - Tyler Reinsch'

    workbook.creator = authorName
    workbook.lastModifiedBy = authorName
    workbook.created = new Date()
    workbook.modified = new Date()

    workbook.title = 'ATLAS TLR Export'
    workbook.subject = 'PubMed literature review export'
    workbook.company = 'Arysana'
    workbook.manager = 'Arysana'
    workbook.keywords = 'Arysana, ATLAS, PubMed, literature review'
    workbook.description = "Generated with Arysana's ATLAS"

    await addSummarySheet(workbook, workbookData)

    addDashboardSheet(
        workbook,
        summaryMetrics,
        shareOfVoiceRows,
        shareOfVoiceByYearRows,
        categoryByCompanyRows,
        outcomeByCompanyRows,
        messagingByCompanyRows
    )

    const tlrWorksheet = addWorksheetFromRows(workbook, 'TLR Review', tlrRows, TLR_COLUMNS, [
        { key: 'fullTextLink', text: 'Open full text' },
    ])

    const titleScreeningCol = TLR_COLUMNS.findIndex((c) => c.key === 'titleScreening') + 1
    const abstractScreeningCol = TLR_COLUMNS.findIndex((c) => c.key === 'abstractScreening') + 1
    const fullTextScreeningCol = TLR_COLUMNS.findIndex((c) => c.key === 'fullTextScreening') + 1

    addScreeningValidation(tlrWorksheet, tlrRows.length, [
        titleScreeningCol,
        abstractScreeningCol,
        fullTextScreeningCol,
    ])

    addWorksheetFromRows(workbook, 'Extraction Lab', extractionRows, EXTRACTION_COLUMNS, [
        { key: 'fullTextLink', text: 'Open full text' },
    ])

    addConfigSheet(workbook, configRows)

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    saveAs(blob, filename)
}
